// The original one-line race graph (as of 29040e9, 2026-06-23), restored as
// the traditional view (the default) behind RaceView's traditional / advanced
// toggle. The one-row-per-racer graph in RaceTimeline.tsx is the advanced view.
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

/** One card effect behind a racer's boost (mirrors api/types RaceAppliedEffect). */
interface AppliedEffect {
    cardSlug: string;
    cardName?: string | null;
    deltaMs: number;
    reason: string;
}

interface RaceParticipant {
    username: string;
    /** Signature emoji shown in place of the username (falls back to username). */
    userEmoji?: string | null;
    attemptedAt: string;
    offsetMs: number;
    deltaMsFromPrev: number;
    /** "early" rows clicked before unlock — rendered red, to the left of the unlock marker.
     *  "blocked" rows were vetoed by a Gridlock card — rendered as a 🔒 lock cluster. */
    outcome?: "normal" | "early" | "blocked";
    /** Slug of the card that blocked this attempt (Gridlock → "gridlock"). */
    blockedByCardSlug?: string | null;
    /** Usernames of the player(s) whose Gridlock blocked this attempt. */
    blockedByUsernames?: string[];
    displayHidden?: boolean;
    /** Negative ms head start applied by a respect card (e.g. -145), or null. */
    appliedAdvantageMs?: number | null;
    /** Slug of the card that granted the advantage (e.g. "sprint-lead"). */
    appliedAdvantageCardSlug?: string | null;
    /** Per-effect breakdown behind appliedAdvantageMs — absent on legacy days. */
    appliedEffects?: AppliedEffect[] | null;
    /** PRE-effects offset from unlockAt (the actual tap, before card shifts). */
    effectiveOffsetMs?: number | null;
    /** How the effective time was derived — null on legacy rows. */
    clampVerdict?:
        | "none"
        | "high"
        | "server"
        | "latency_corrected"
        | "arrival_fallback"
        | null;
    /** Where the request physically landed (serverArrival − unlock, ms). */
    arrivalOffsetMs?: number | null;
    /** WS-measured one-way latency credited back (aotdrace-2.0). */
    oneWayLatencyMs?: number | null;
    /** False = no usable ping window; ranked on raw arrival. */
    latencyEstimated?: boolean | null;
    /** "late_arrival" = tapped in the 45s contest window after the claim. */
    abortReason?: string | null;
}

interface RaceData {
    unlockAt: string;
    author: RaceParticipant;
    completedAt: string;
    participants: RaceParticipant[];
}

interface RaceTimelineProps {
    race: RaceData;
    generationTimeMs?: number;
}

const TIMELINE_DURATION_MS = 45_000;
/** Pre-unlock clicks more than 4.5s early are audit-only — not shown on the chart. */
const EARLY_TIMELINE_FLOOR_MS = -4_500;

// the smallest gap gets at least this % and at most MAX_BASE_PCT
const MIN_BASE_PCT = 10;
const MAX_BASE_PCT = 20;
// if a gap exceeds COMPRESS_FACTOR * the smallest gap, it becomes "..."
const COMPRESS_FACTOR = 4;
// fixed visual width of each compressed "..." section — wide enough that two
// labels on the same side of the line don't collide across a single break
const COMPRESSED_WIDTH = 16;
// half-width of the visual line break at each "..." marker
const BREAK_HALF = 4;
// minimum solid-dot ↔ ghost-dot separation before the ghost is drawn — under
// this (~2px on a typical track width) the pair reads as a smudge, not a span
const GHOST_MIN_SEP_PCT = 0.6;

// Latency/clamp verdict chips were removed from the public timeline (the "how
// much was the time adjusted" detail lives in the admin panel now). The
// timeline keeps only what a viewer cares about: card head starts, early
// clicks, and late arrivals.

const LATE_EXPLANATION =
    "arrived inside the 45s contest window, but the day was already claimed — counted as a contestant";

// ---- styled components ----

const TimelineContainer = styled.div`
  margin-top: 12px;
  padding: 8px 0;
  width: 100%;
`;

const TimelineLabel = styled.div`
  font-size: 0.75rem;
  color: #9ca3af;
  margin-bottom: 6px;
  font-style: italic;
`;

// min-height scales with how many pills the busiest racer stacks below its
// time (see `labelMinHeight`): a plain race stays compact at 92px; badged
// races reserve room so the stacked BadgeRow can't bleed into the next section
// (nothing clips overflow here).
const TimelineWrapper = styled.div<{ $minHeight: number }>`
  display: flex;
  align-items: center;
  min-height: ${props => props.$minHeight}px;
  width: 100%;
  gap: 6px;
`;

const TimelineTrack = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
`;

const LineSegment = styled.div<{ $left: number; $right: number }>`
  position: absolute;
  top: 50%;
  left: ${props => props.$left}%;
  width: ${props => Math.max(0, props.$right - props.$left)}%;
  height: 2px;
  background: #ddd;
  border-radius: 2px;
  transform: translateY(-50%);
`;

const GapMarker = styled.div<{ $position: number }>`
  position: absolute;
  left: ${props => props.$position}%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 5px;
  pointer-events: none;

  & > span {
    width: 4px;
    height: 4px;
    border-radius: 50%;
    background: #aaa;
  }
`;

const ParticipantMarker = styled.div<{ $position: number }>`
  position: absolute;
  left: ${props => props.$position}%;
  top: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
`;

// thin vertical line linking a dot to its (above/below) label so the
// alternating layout reads as deliberate rather than crooked
const Stem = styled.div<{ $above?: boolean }>`
  position: absolute;
  left: 50%;
  width: 1px;
  height: 9px;
  background: #d7dae0;
  transform: translateX(-50%);
  ${props => props.$above ? 'bottom: 5px;' : 'top: 5px;'}
`;

const MarkerDot = styled.div<{ $boosted?: boolean; $late?: boolean }>`
  width: 8px;
  height: 8px;
  background: ${props => (props.$late ? '#b6bcc6' : '#666')};
  border-radius: 50%;
  border: 2px solid #fff;
  opacity: ${props => (props.$late ? 0.75 : 1)};
  box-shadow: ${props => props.$boosted
    ? '0 0 0 1px #f59e0b, 0 0 0 4px rgba(245, 158, 11, 0.25)'
    : props.$late
      ? '0 0 0 1px #b6bcc6'
      : '0 0 0 1px #666'};
`;

const MarkerLabel = styled.div<{ $above?: boolean }>`
  font-size: 0.65rem;
  color: #555;
  white-space: nowrap;
  position: absolute;
  ${props => props.$above ? 'bottom: 14px;' : 'top: 14px;'}
  transform: translateX(-50%);
  left: 50%;
  text-align: center;
  line-height: 1.2;
`;

const Username = styled.span`
  font-weight: 600;
  display: block;
`;

const ActualTime = styled.span`
  font-size: 0.55rem;
  color: #666;
  display: block;
`;

// small pill shown under a participant when a respect card shifted their
// arrival earlier (e.g. "⚡ 145ms" — Sprint Lead family). Tappable: pins a
// per-effect breakdown popover (hover keeps the plain title tooltip).
const AdvantageBadge = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 5px;
  font-size: 0.5rem;
  font-weight: 700;
  line-height: 1.5;
  color: #b45309;
  background: #fffbeb;
  border: 1px solid #fcd34d;
  border-radius: 999px;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

// Status chip shown UNDER the time inside `BadgeRow` (clamp verdict / latency
// credit / late arrival). `position: relative` is load-bearing — the tap-pinned
// PinPopover anchors to it — so keep it even though the badge no longer sits
// inline after the time (BadgeRow's gap now does the spacing the old
// margin-left / vertical-align used to).
const ClampBadge = styled.span`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 0 4px;
  font-size: 0.5rem;
  font-weight: 700;
  line-height: 1.5;
  color: #475569;
  background: #f1f5f9;
  border: 1px solid #cbd5e1;
  border-radius: 999px;
  white-space: nowrap;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
`;

// Holds every status/advantage pill for a racer, stacked on their own row
// BELOW the time instead of inline after it. Collapsing the pills off the time
// line shrinks each label's horizontal footprint to ~the widest pill, so
// neighbouring labels stop colliding (the user-reported "tags block things").
// The max-width is what actually makes flex-wrap engage: MarkerLabel is
// position:absolute with no width, so without a bound the row never wraps and
// two pills stay one wide line. 96px fits the widest single pill
// ("📡 raw arrival") and forces a second line once two pills don't fit.
const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 3px;
  margin-top: 3px;
  max-width: 96px;
`;

// Tap-pinned info bubble anchored to its chip (advantage breakdown / clamp
// explanation). pointer-events: none like RevealName — a tap anywhere else
// lands on the document and the outside-tap listener dismisses it.
const PinPopover = styled.span<{ $above?: boolean }>`
  position: absolute;
  ${props => props.$above ? 'bottom: calc(100% + 5px);' : 'top: calc(100% + 5px);'}
  left: 50%;
  transform: translateX(-50%);
  width: 178px;
  padding: 6px 8px;
  font-size: 0.6rem;
  font-weight: 500;
  line-height: 1.45;
  text-align: left;
  color: #fff;
  background: rgba(31, 41, 55, 0.96);
  border-radius: 6px;
  white-space: normal;
  pointer-events: none;
  z-index: 6;
`;

const PopoverRow = styled.span`
  display: flex;
  justify-content: space-between;
  gap: 8px;
`;

const PopoverSummary = styled.span`
  display: block;
  margin-top: 4px;
  padding-top: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
  color: #fcd34d;
`;

// faint secondary dot at a boosted racer's pre-boost ("raw tap") position —
// the solid dot sits at the contest position, the ghost shows where they
// actually tapped before the card shifted them
const GhostDot = styled.div<{ $position: number }>`
  position: absolute;
  left: ${props => props.$position}%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 6px;
  height: 6px;
  background: #fde68a;
  border-radius: 50%;
  border: 1px solid #f59e0b;
  opacity: 0.7;
  pointer-events: none;
`;

// short amber connector tying the solid (contest) dot to its ghost (raw tap)
const GhostConnector = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  top: 50%;
  left: ${props => props.$left}%;
  width: ${props => Math.max(0, props.$width)}%;
  height: 2px;
  background: rgba(245, 158, 11, 0.4);
  transform: translateY(-50%);
  pointer-events: none;
`;

// Wraps the emoji label so it reveals the username on hover (desktop) or
// when pinned via tap (mobile). The emoji is the resting state; the username
// surfaces in `RevealName`.
const NameToggle = styled.span<{ $clickable?: boolean }>`
  position: relative;
  display: inline-block;
  cursor: ${props => (props.$clickable ? 'pointer' : 'default')};
  -webkit-tap-highlight-color: transparent;

  & .reveal-name { display: none; }
  ${props => props.$clickable && `
    &:hover .reveal-name { display: block; }
    &.pinned .reveal-name { display: block; }
  `}
`;

// The username bubble shown on hover / tap. Floats above the emoji so it
// never disturbs the timeline's tight horizontal layout.
const RevealName = styled.span`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 4px;
  padding: 2px 6px;
  font-size: 0.6rem;
  font-weight: 600;
  line-height: 1.3;
  color: #fff;
  background: rgba(31, 41, 55, 0.95);
  border-radius: 5px;
  white-space: nowrap;
  pointer-events: none;
  z-index: 5;
`;

const EmojiGlyph = styled.span`
  font-size: 0.95rem;
  line-height: 1;
  display: block;
`;

const UnlockDot = styled(MarkerDot)`
  background: #ddd;
  box-shadow: 0 0 0 1px #bbb;
`;

const AuthorDot = styled(MarkerDot)`
  background: #6366f1;
  box-shadow: ${props => props.$boosted
    ? '0 0 0 1px #f59e0b, 0 0 0 4px rgba(245, 158, 11, 0.25)'
    : '0 0 0 1px #6366f1'};
`;

const UnlockMarker = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const EarlyCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-right: 6px;
  border-right: 1px dashed rgba(255, 59, 59, 0.5);
`;

const EarlyEntry = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 0.6rem;
  color: #ff3b3b;
`;

const EarlyDot = styled.div`
  width: 8px;
  height: 8px;
  background: #ff3b3b;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px #ff3b3b;
`;

const EarlyUsername = styled.span`
  font-weight: 600;
`;

const EarlyOffset = styled.span`
  font-size: 0.55rem;
`;

const BlockedCluster = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding-left: 6px;
  border-left: 1px dashed rgba(180, 180, 190, 0.5);
`;

const BlockedEntry = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 0.6rem;
  color: #9aa0aa;
`;

const BlockedDot = styled.div`
  font-size: 0.85rem;
  line-height: 1;
  filter: grayscale(0.2);
`;

const BlockedUsername = styled.span`
  font-weight: 600;
  text-decoration: line-through;
  text-decoration-color: rgba(154, 160, 170, 0.7);
`;

const BlockedBy = styled.span`
  font-size: 0.55rem;
  color: #c98a8a;
  white-space: nowrap;
`;

const UnlockTimeLabel = styled.span`
  font-size: 0.6rem;
  color: #888;
  white-space: nowrap;
`;

const EndMarker = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

// ---- helpers ----

function formatLocalTime(isoString: string, showMs: boolean): string {
    const d = new Date(isoString);
    const h = d.getHours().toString().padStart(2, '0');
    const m = d.getMinutes().toString().padStart(2, '0');
    const s = d.getSeconds();
    const ms = d.getMilliseconds();
    if (showMs) {
        return `${h}:${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`;
    }
    // drop seconds when they're zero for cleaner labels
    if (s === 0 && ms === 0) return `${h}:${m}`;
    return `${h}:${m}:${s.toString().padStart(2, '0')}`;
}

// Compact label for on-track markers. The whole race window (unlock + 45s)
// lives inside a single minute, and the unlock marker already prints "HH:MM",
// so participant labels only need seconds + ms — dropping the repeated
// "HH:MM:" prefix that otherwise makes neighbouring labels collide. Most taps
// land in the first second, so those read as a bare ms value ("492ms") instead
// of a zero-padded "00.492s"; once a tap crosses a second we switch to "S.mmms".
function formatTrackTime(isoString: string, showMs: boolean): string {
    const d = new Date(isoString);
    const s = d.getSeconds();
    if (showMs) {
        if (s === 0) return `${d.getMilliseconds()}ms`;
        // Keep the ms readout users like, just drop the ugly leading zero on the
        // seconds: "1.234s" / "8.216s" instead of "01.234s".
        const ms = d.getMilliseconds().toString().padStart(3, '0');
        return `${s}.${ms}s`;
    }
    return `${s}s`;
}

// "sprint-lead" -> "Sprint Lead" for the advantage-badge tooltip
function prettifyCardSlug(slug: string): string {
    return slug
        .replace(/[-_]+/g, ' ')
        .replace(/\b\w/g, c => c.toUpperCase());
}

function formatSignedMs(ms: number): string {
    return ms > 0 ? `+${ms}ms` : `${ms}ms`;
}

// Effects behind a racer's boost pill. Prefers the per-effect breakdown from
// the new wire contract; legacy backends only send the aggregate
// appliedAdvantageMs/CardSlug pair, surfaced as one synthesized row.
function effectsFor(evt: EventWithFlag): AppliedEffect[] {
    if (Array.isArray(evt.appliedEffects) && evt.appliedEffects.length > 0) {
        return evt.appliedEffects;
    }
    if (typeof evt.appliedAdvantageMs === 'number' && evt.appliedAdvantageMs !== 0) {
        return [{
            cardSlug: evt.appliedAdvantageCardSlug ?? 'card',
            cardName: null,
            deltaMs: evt.appliedAdvantageMs,
            reason: 'advantage',
        }];
    }
    return [];
}

function effectDisplayName(eff: AppliedEffect): string {
    return (eff.cardName && eff.cardName.trim()) || prettifyCardSlug(eff.cardSlug);
}

// PRE-effects ("raw tap") offset from unlock. `effectiveOffsetMs` is
// authoritative when the backend sends it; legacy rows derive it from the
// aggregate advantage (advantage is negative, so the raw tap is later).
function rawOffsetFor(evt: EventWithFlag): number | null {
    if (typeof evt.effectiveOffsetMs === 'number') return evt.effectiveOffsetMs;
    if (typeof evt.appliedAdvantageMs === 'number' && evt.appliedAdvantageMs !== 0) {
        return evt.offsetMs - evt.appliedAdvantageMs;
    }
    return null;
}

// ---- layout types ----

type EventWithFlag = {
    username: string;
    userEmoji?: string | null;
    attemptedAt: string;
    offsetMs: number;
    deltaMsFromPrev: number;
    isAuthor: boolean;
    displayHidden?: boolean;
    appliedAdvantageMs?: number | null;
    appliedAdvantageCardSlug?: string | null;
    appliedEffects?: AppliedEffect[] | null;
    effectiveOffsetMs?: number | null;
    clampVerdict?:
        | "none"
        | "high"
        | "server"
        | "latency_corrected"
        | "arrival_fallback"
        | null;
    arrivalOffsetMs?: number | null;
    oneWayLatencyMs?: number | null;
    latencyEstimated?: boolean | null;
    abortReason?: string | null;
};

interface PositionedEvent extends EventWithFlag {
    pct: number;
}

interface LayoutResult {
    events: PositionedEvent[];
    segments: { left: number; right: number }[];
    gapMarkers: number[];
    showMs: boolean;
}

// ---- component ----

const RaceTimelineBasic: FC<RaceTimelineProps> = ({ race, generationTimeMs }) => {
    // Split out early-outcome participants — they get rendered as a red
    // cluster to the LEFT of the unlock marker, with negative ms labels.
    // They do not participate in the proportional timeline layout.
    const earlyEntries = useMemo(
        () => race.participants
            .filter(p =>
                p.outcome === "early"
                && typeof p.offsetMs === "number"
                && p.offsetMs >= EARLY_TIMELINE_FLOOR_MS
                && p.offsetMs < 0,
            )
            .sort((a, b) => a.offsetMs - b.offsetMs),
        [race.participants],
    );
    const onTimeParticipants = useMemo(
        () => race.participants.filter(p => p.outcome !== "early" && p.outcome !== "blocked"),
        [race.participants],
    );
    // Blocked attempts (Gridlock): vetoed before they could enter the race, so
    // they never belong on the proportional track. Surface them as a separate
    // 🔒 cluster with the blocker's name instead of a normal contender dot.
    const blockedEntries = useMemo(
        () => race.participants.filter(p => p.outcome === "blocked"),
        [race.participants],
    );

    const layout = useMemo((): LayoutResult | null => {
        const author = race.author;
        const participants = [...onTimeParticipants].sort((a, b) => a.offsetMs - b.offsetMs);

        if (!author || participants.length < 1) return null;

        // only include participants within generationTime + 45s
        const cutoffMs = generationTimeMs != null
            ? generationTimeMs + TIMELINE_DURATION_MS
            : Infinity;
        const filtered = participants.filter(p => p.offsetMs <= cutoffMs);
        if (filtered.length < 1) return null;

        // merge and sort all events
        const allEvents: EventWithFlag[] = [
            { ...author, isAuthor: true },
            ...filtered.map(p => ({ ...p, isAuthor: false }))
        ].sort((a, b) => a.offsetMs - b.offsetMs);

        return computeLayout(allEvents);
    }, [race.author, onTimeParticipants, generationTimeMs]);

    const unlockAtMs = useMemo(() => new Date(race.unlockAt).getTime(), [race.unlockAt]);

    // Ghost markers: a faint dot at each boosted racer's pre-boost ("raw tap")
    // position, tied to the solid dot by an amber connector so the head start
    // is visible as a span on the track. Hidden rows carry no advantage data
    // (the backend nulls it) and near-overlapping pairs are skipped.
    const ghosts = useMemo(() => {
        if (!layout) return [];
        const out: { username: string; fromPct: number; toPct: number }[] = [];
        for (const evt of layout.events) {
            if (evt.displayHidden) continue;
            const rawOffsetMs = rawOffsetFor(evt);
            if (rawOffsetMs === null || rawOffsetMs === evt.offsetMs) continue;
            const toPct = offsetToPct(layout.events, rawOffsetMs);
            if (Math.abs(toPct - evt.pct) < GHOST_MIN_SEP_PCT) continue;
            out.push({ username: evt.username, fromPct: evt.pct, toPct });
        }
        return out;
    }, [layout]);

    // How tall the busiest label is: count the pills the most-decorated racer
    // stacks below its time and reserve vertical room so the stacked BadgeRow
    // never bleeds into the section beneath the chart (nothing clips overflow).
    // Only card head starts and late arrivals carry a pill now (latency/clamp
    // chips were removed). 0 pills → compact 92px; 1 → 114px; 2 → 136px.
    const labelMinHeight = useMemo(() => {
        if (!layout) return 92;
        let maxPills = 0;
        for (const evt of layout.events) {
            let n = 0;
            if (evt.abortReason === 'late_arrival') n += 1;
            if (effectsFor(evt).reduce((s, e) => s + e.deltaMs, 0) !== 0) n += 1;
            if (n > maxPills) maxPills = n;
        }
        return maxPills === 0 ? 92 : maxPills === 1 ? 114 : 136;
    }, [layout]);

    // Tap-to-reveal (mobile): which racer's username bubble is currently
    // pinned open. Desktop reveals on hover via CSS; this drives the tap path.
    const [pinnedKey, setPinnedKey] = useState<string | null>(null);
    const togglePin = useCallback((key: string) => {
        setPinnedKey(prev => (prev === key ? null : key));
    }, []);
    // Dismiss a pinned bubble when tapping anywhere else on the page.
    useEffect(() => {
        if (pinnedKey === null) return;
        const clear = () => setPinnedKey(null);
        document.addEventListener('click', clear);
        return () => document.removeEventListener('click', clear);
    }, [pinnedKey]);

    // Renders a racer's identity: the emoji is the resting label, and the
    // username surfaces on hover (desktop) or tap (mobile). Falls back to the
    // bare username when the user hasn't set a signature emoji.
    const renderIdentity = (
        key: string,
        username: string,
        emoji?: string | null,
    ) => {
        const glyph = emoji && emoji.trim() ? emoji.trim() : null;
        if (!glyph) return <>{username}</>;
        return (
            <NameToggle
                $clickable
                className={pinnedKey === key ? 'pinned' : ''}
                title={username}
                onClick={(e) => {
                    e.stopPropagation();
                    togglePin(key);
                }}
            >
                <EmojiGlyph>{glyph}</EmojiGlyph>
                <RevealName className="reveal-name">{username}</RevealName>
            </NameToggle>
        );
    };

    // A "blocked-only" (Gridlock) or "early-only" day has no proportional
    // layout (no on-track participant) but still has a cluster to show, so
    // only bail when there's nothing at all to render.
    if (!layout && earlyEntries.length === 0 && blockedEntries.length === 0) return null;

    return (
        <TimelineContainer>
            <TimelineLabel>race to the aotd</TimelineLabel>
            <TimelineWrapper $minHeight={labelMinHeight}>
                {earlyEntries.length > 0 && (
                    <EarlyCluster>
                        {earlyEntries.map(e => (
                            <EarlyEntry key={`early-${e.username}`} title={`${e.username} clicked ${Math.abs(e.offsetMs)}ms before unlock`}>
                                <EarlyDot />
                                <EarlyUsername>
                                    {renderIdentity(`early-${e.username}`, e.username, e.userEmoji)}
                                </EarlyUsername>
                                <EarlyOffset>{e.offsetMs}ms</EarlyOffset>
                            </EarlyEntry>
                        ))}
                    </EarlyCluster>
                )}
                {layout && (
                  <>
                <UnlockMarker>
                    <UnlockTimeLabel>{formatLocalTime(race.unlockAt, false)}</UnlockTimeLabel>
                    <UnlockDot />
                </UnlockMarker>

                <TimelineTrack>
                    {layout.segments.map((seg, i) => (
                        <LineSegment key={i} $left={seg.left} $right={seg.right} />
                    ))}

                    {layout.gapMarkers.map((pos, i) => (
                        <GapMarker key={`g${i}`} $position={pos}>
                            <span /><span /><span />
                        </GapMarker>
                    ))}

                    {ghosts.map(g => (
                        <React.Fragment key={`ghost-${g.username}`}>
                            <GhostConnector
                                $left={Math.min(g.fromPct, g.toPct)}
                                $width={Math.abs(g.toPct - g.fromPct)}
                            />
                            <GhostDot $position={g.toPct} />
                        </React.Fragment>
                    ))}

                    {layout.events.map((evt, i) => {
                        const above = i % 2 === 1;
                        const effects = effectsFor(evt);
                        const netAdvantageMs = effects.reduce((s, e) => s + e.deltaMs, 0);
                        const advantageMs = Math.abs(netAdvantageMs);
                        const effectNames = effects.map(effectDisplayName);
                        const rawOffsetMs = rawOffsetFor(evt);
                        const advKey = `adv-${evt.username}`;
                        const lateKey = `late-${evt.username}`;
                        const isLate = evt.abortReason === 'late_arrival';
                        return (
                            <ParticipantMarker key={evt.username} $position={evt.pct} title={evt.username}>
                                <Stem $above={above} />
                                {evt.isAuthor
                                    ? <AuthorDot $boosted={advantageMs > 0} />
                                    : <MarkerDot $boosted={advantageMs > 0} $late={isLate} />}
                                <MarkerLabel $above={above}>
                                    <Username>{renderIdentity(evt.username, evt.username, evt.userEmoji)}</Username>
                                    <ActualTime>
                                        {formatTrackTime(evt.attemptedAt, layout.showMs)}
                                    </ActualTime>
                                    {(isLate || advantageMs > 0) && (
                                      <BadgeRow>
                                        {isLate && (
                                            <ClampBadge
                                                title={LATE_EXPLANATION}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePin(lateKey);
                                                }}
                                            >
                                                🕘 late
                                                {pinnedKey === lateKey && (
                                                    <PinPopover $above={above}>
                                                        {LATE_EXPLANATION}
                                                    </PinPopover>
                                                )}
                                            </ClampBadge>
                                        )}
                                        {advantageMs > 0 && (
                                            <AdvantageBadge
                                                title={`${effectNames.join(' + ')} gave a ${advantageMs}ms head start`}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    togglePin(advKey);
                                                }}
                                            >
                                                ⚡ {advantageMs}ms
                                                {pinnedKey === advKey && (
                                                    <PinPopover $above={above}>
                                                        {effects.map((eff, j) => (
                                                            <PopoverRow key={`${eff.cardSlug}-${j}`}>
                                                                <span>{effectDisplayName(eff)}</span>
                                                                <span>{formatSignedMs(eff.deltaMs)}</span>
                                                            </PopoverRow>
                                                        ))}
                                                        {rawOffsetMs !== null && (
                                                            <PopoverSummary>
                                                                tapped at {formatTrackTime(new Date(unlockAtMs + rawOffsetMs).toISOString(), true)}
                                                                {' → ranked at '}
                                                                {formatTrackTime(evt.attemptedAt, true)}
                                                            </PopoverSummary>
                                                        )}
                                                    </PinPopover>
                                                )}
                                            </AdvantageBadge>
                                        )}
                                      </BadgeRow>
                                    )}
                                </MarkerLabel>
                            </ParticipantMarker>
                        );
                    })}
                </TimelineTrack>

                <EndMarker>
                    <UnlockDot />
                    <UnlockTimeLabel>
                        {formatLocalTime(
                            new Date(new Date(race.unlockAt).getTime() + TIMELINE_DURATION_MS).toISOString(),
                            false
                        )}
                    </UnlockTimeLabel>
                </EndMarker>
                  </>
                )}

                {blockedEntries.length > 0 && (
                    <BlockedCluster>
                        {blockedEntries.map(b => {
                            const blockers = (b.blockedByUsernames ?? []).filter(Boolean);
                            const byLabel = blockers.length > 0 ? `by ${blockers.join(', ')}` : 'blocked';
                            const cardLabel = b.blockedByCardSlug ?? 'a card';
                            return (
                                <BlockedEntry
                                    key={`blocked-${b.username}`}
                                    title={`${b.username}'s claim was blocked by ${cardLabel}${blockers.length ? ` (${blockers.join(', ')})` : ''}`}
                                >
                                    <BlockedDot>🔒</BlockedDot>
                                    <BlockedUsername>
                                        {renderIdentity(`blocked-${b.username}`, b.username, b.userEmoji)}
                                    </BlockedUsername>
                                    <BlockedBy>{byLabel}</BlockedBy>
                                </BlockedEntry>
                            );
                        })}
                    </BlockedCluster>
                )}
            </TimelineWrapper>
        </TimelineContainer>
    );
};

// ---- layout algorithm ----
// single-pass: compute gaps including unlock->first, classify each as
// proportional (smallest gap = BASE_PCT) or compressed ("..."), then lay out.

function computeLayout(allEvents: EventWithFlag[]): LayoutResult {
    // gaps include unlock(0) -> first event
    const gapMs: number[] = [Math.max(1, allEvents[0].offsetMs)];
    for (let i = 1; i < allEvents.length; i++) {
        gapMs.push(Math.max(1, allEvents[i].offsetMs - allEvents[i - 1].offsetMs));
    }

    const minGap = Math.min(...gapMs);
    const threshold = minGap * COMPRESS_FACTOR;

    // trailing gap = time from last event to 45s
    const lastEventMs = allEvents[allEvents.length - 1].offsetMs;
    const trailingMs = Math.max(1, TIMELINE_DURATION_MS - lastEventMs);
    const allGapMs = [...gapMs, trailingMs];

    // classify each gap including trailing (same 4x rule for all)
    const gapTypes: ('prop' | 'compressed')[] = allGapMs.map(g =>
        g > threshold ? 'compressed' : 'prop'
    );

    // compute ratio of each proportional gap to the smallest gap
    const propRatios = allGapMs.map((g, i) =>
        gapTypes[i] === 'prop' ? g / minGap : 0
    );
    const totalRatio = propRatios.reduce((s, r) => s + r, 0);

    // find the ideal base so proportional content fills available space
    const numCompressed = gapTypes.filter(t => t === 'compressed').length;
    const minCompressedSpace = numCompressed * COMPRESSED_WIDTH;
    const availableForProp = 100 - minCompressedSpace;

    const basePct = totalRatio > 0
        ? Math.min(MAX_BASE_PCT, Math.max(MIN_BASE_PCT, availableForProp / totalRatio))
        : MIN_BASE_PCT;

    const propWidths = propRatios.map(r => r * basePct);
    const totalProp = propWidths.reduce((s, w) => s + w, 0);

    // compressed sections share remaining space proportional to their actual ms
    const compressedMs = allGapMs
        .filter((_, i) => gapTypes[i] === 'compressed');
    const totalCompressedMs = compressedMs.reduce((s, g) => s + g, 0);
    const spaceForCompressed = Math.max(0, 100 - totalProp);

    // compute per-slot compressed widths, proportional to time, with a minimum
    const compressedWidths = allGapMs.map((g, i) => {
        if (gapTypes[i] !== 'compressed') return 0;
        const share = totalCompressedMs > 0
            ? (g / totalCompressedMs) * spaceForCompressed
            : spaceForCompressed / compressedMs.length;
        return Math.max(share, COMPRESSED_WIDTH);
    });

    // if everything overflows, scale proportional gaps down. when the
    // compressed widths alone already exceed (or nearly exceed) 100%, also
    // scale the compressed widths down so we keep a small reserve for the
    // proportional gaps — otherwise `scale` would go negative and the cursor
    // would walk backwards, visually swapping closely-spaced events.
    const totalNeeded = totalProp + compressedWidths.reduce((s, w) => s + w, 0);
    let scale = 1;
    if (totalNeeded > 100) {
        let fixedTotal = compressedWidths.reduce((s, w) => s + w, 0);
        const minPropReserve = totalProp > 0 ? Math.min(totalProp, MIN_BASE_PCT) : 0;
        if (fixedTotal + minPropReserve > 100) {
            // compressed widths consume all/most space — proportionally
            // scale them down to leave `minPropReserve` for prop gaps
            const target = Math.max(0, 100 - minPropReserve);
            const cs = fixedTotal > 0 ? target / fixedTotal : 1;
            for (let i = 0; i < compressedWidths.length; i++) {
                compressedWidths[i] *= cs;
            }
            fixedTotal = compressedWidths.reduce((s, w) => s + w, 0);
        }
        scale = totalProp > 0 ? Math.max(0, (100 - fixedTotal) / totalProp) : 1;
    }

    // build final widths for each gap slot (including trailing)
    const widths = allGapMs.map((_, i) =>
        gapTypes[i] === 'compressed' ? compressedWidths[i] : propWidths[i] * scale
    );

    // build positioned events and gap markers
    const events: PositionedEvent[] = [];
    const gapMarkers: number[] = [];
    let cursor = 0;

    for (let i = 0; i < allEvents.length; i++) {
        const w = widths[i];

        if (gapTypes[i] === 'compressed') {
            gapMarkers.push(cursor + w / 2);
        }

        cursor += w;
        events.push({ ...allEvents[i], pct: Math.max(2, Math.min(98, cursor)) });
    }

    // trailing gap (only add "..." marker if it's compressed)
    const trailingIdx = widths.length - 1;
    const trailingW = widths[trailingIdx];
    if (gapTypes[trailingIdx] === 'compressed') {
        gapMarkers.push(cursor + trailingW / 2);
    }

    // build line segments: continuous line from 0 to 100, broken at each "..." center
    const segments: { left: number; right: number }[] = [];
    let lineStart = 0;
    for (const gc of gapMarkers) {
        if (gc - BREAK_HALF > lineStart) {
            segments.push({ left: lineStart, right: gc - BREAK_HALF });
        }
        lineStart = gc + BREAK_HALF;
    }
    if (lineStart < 100) {
        segments.push({ left: lineStart, right: 100 });
    }

    // show milliseconds when the smallest gap is under 3 seconds
    const showMs = minGap < 3000;

    return { events, segments, gapMarkers, showMs };
}

// Map a raw ms offset onto the laid-out track. The layout is non-linear
// (compressed "..." gaps), so interpolate piecewise-linearly between the
// known anchors: unlock (0ms → 0%), each positioned event, and the track end.
// Exact inside proportional gaps; a faithful approximation inside compressed
// ones — good enough for the ghost markers, which are illustrative.
function offsetToPct(events: PositionedEvent[], ms: number): number {
    const lastEventMs = events.length > 0 ? events[events.length - 1].offsetMs : 0;
    const anchors = [
        { ms: 0, pct: 0 },
        ...events.map(e => ({ ms: e.offsetMs, pct: e.pct })),
        { ms: Math.max(TIMELINE_DURATION_MS, lastEventMs + 1), pct: 100 },
    ];
    let pct = 100;
    for (let i = 1; i < anchors.length; i++) {
        if (ms <= anchors[i].ms) {
            const a = anchors[i - 1];
            const b = anchors[i];
            const t = b.ms === a.ms ? 1 : (ms - a.ms) / (b.ms - a.ms);
            pct = a.pct + Math.max(0, Math.min(1, t)) * (b.pct - a.pct);
            break;
        }
    }
    // same visual clamp the events themselves get
    return Math.max(2, Math.min(98, pct));
}

export default RaceTimelineBasic;
